import React, { useEffect, useState } from 'react';
import emailjs from '@emailjs/browser';
import {
  EMAILJS_PUBLIC_KEY,
  EMAILJS_SERVICE_ID,
  EMAILJS_TEMPLATE_ID,
  isEmailJsConfigured,
} from '../constants/email';
import { CONTACT_EMAILS, CONTACT_PHONES } from '../constants/site';

const INITIAL_FORM_STATE = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
};

const buildTemplateParams = (formData) => {
  const email = formData.email.trim().toLowerCase();

  return {
    name: formData.name.trim(),
    from_name: formData.name.trim(),
    user_name: formData.name.trim(),
    email,
    from_email: email,
    user_email: email,
    reply_to: email,
    phone: formData.phone.trim(),
    subject: formData.subject.trim(),
    title: formData.subject.trim(),
    message: formData.message.trim(),
  };
};

const getEmailErrorMessage = (sendError) => {
  const detail = sendError?.text || '';

  if (/public key is invalid|user_id parameter/i.test(detail)) {
    return 'Email is not configured yet (invalid Public Key). Please email or call us directly using the details below.';
  }

  if (/ENOTFOUND|SMTP|mail server|getaddrinfo/i.test(detail)) {
    return 'The email service is not configured correctly (mail server not found). Please email or call us directly using the details below.';
  }

  if (/412|Gmail|Invalid grant|insufficient authentication/i.test(detail)) {
    return 'The email service needs to be reconnected in EmailJS. Please email or call us directly using the details below.';
  }

  if (detail) {
    return `${detail} Please email or call us directly using the details below.`;
  }

  return 'Failed to send email. Please email or call us directly using the details below.';
};

const ContactForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (EMAILJS_PUBLIC_KEY) {
      emailjs.init({ publicKey: EMAILJS_PUBLIC_KEY });
    }
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (!isEmailJsConfigured()) {
      setError(
        'Email is not configured yet. Please email or call us directly using the details below.'
      );
      setLoading(false);
      return;
    }

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        buildTemplateParams(formData),
        { publicKey: EMAILJS_PUBLIC_KEY }
      );

      setFormData(INITIAL_FORM_STATE);
      setSuccess('Your message was sent successfully. We will get back to you soon.');
    } catch (sendError) {
      console.error('EmailJS error:', sendError);
      setError(getEmailErrorMessage(sendError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="input-flex">
        <div className="inputBx">
          <input
            type="text"
            name="name"
            placeholder="Enter your name..."
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>
        <div className="inputBx">
          <input
            type="email"
            name="email"
            placeholder="Enter your email..."
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="input-flex">
        <div className="inputBx">
          <input
            type="tel"
            name="phone"
            placeholder="Enter your phone..."
            value={formData.phone}
            onChange={handleChange}
          />
        </div>
        <div className="inputBx">
          <input
            type="text"
            name="subject"
            placeholder="Enter your Subject..."
            value={formData.subject}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="inputBx">
        <textarea
          name="message"
          cols="30"
          rows="10"
          placeholder="Message..."
          value={formData.message}
          onChange={handleChange}
          required
        />
      </div>

      <div className="input-button">
        <input type="submit" value={loading ? 'Sending...' : 'Send'} disabled={loading} />
      </div>
      {success && <div className="success-message">{success}</div>}
      {error && (
        <div className="contact-form-feedback">
          <p className="error-message">{error}</p>
          <ul className="contact-form-fallback">
            {CONTACT_EMAILS.map((email) => (
              <li key={email}>
                <a href={`mailto:${email}`}>{email}</a>
              </li>
            ))}
            {CONTACT_PHONES.map((phone) => (
              <li key={phone}>
                <a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  );
};

export default ContactForm;
